import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCampaigns, useCreateLead, useCustomFields } from '../api/hooks';
import { CustomFieldSet } from '../components/CustomFieldInput';
import { ErrorBanner, Modal } from '../components/ui';

export function NewLeadModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** `merged`: the number already had a lead, and this entry went onto it. */
  onCreated: (id: string, merged: boolean) => void;
}) {
  const { data: fields = [] } = useCustomFields();
  const createLead = useCreateLead();

  const [core, setCore] = useState({ full_name: '', phone: '', email: '' });
  const [custom, setCustom] = useState<Record<string, unknown>>({});
  const [campaignId, setCampaignId] = useState('');
  const { data: campaigns = [] } = useCampaigns();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    try {
      const lead = await createLead.mutateAsync({
        full_name: core.full_name.trim(),
        phone: core.phone.trim() || null,
        email: core.email.trim() || null,
        source: 'manual',
        campaign_id: campaignId || null,
        custom_fields: custom,
      });
      onCreated(lead.id, Boolean((lead as { merged?: boolean }).merged));
    } catch (err) {
      setError(err);
      // The API returns details[] keyed by input name — core fields by their
      // own name, custom fields by api_key — so they attach directly.
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
    }
  }

  return (
    <Modal
      title="New lead"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">Cancel</button>
          <button className="btn btn-primary" form="new-lead" disabled={createLead.isPending}>
            {createLead.isPending ? <span className="spinner" /> : 'Create lead'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} />

      <form id="new-lead" onSubmit={submit}>
        <div className={`field${fieldErrors.full_name ? ' invalid' : ''}`}>
          <label htmlFor="full_name">Full name<span className="req">*</span></label>
          <input
            id="full_name"
            required
            autoFocus
            value={core.full_name}
            onChange={(e) => setCore({ ...core, full_name: e.target.value })}
          />
          {fieldErrors.full_name && <div className="err">{fieldErrors.full_name}</div>}
        </div>

        <div className="grid-2">
          <div className={`field${fieldErrors.phone ? ' invalid' : ''}`}>
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              type="tel"
              placeholder="98765 43210"
              value={core.phone}
              onChange={(e) => setCore({ ...core, phone: e.target.value })}
            />
            <div className="hint">Any format — normalised to +91… on save</div>
            {fieldErrors.phone && <div className="err">{fieldErrors.phone}</div>}
          </div>

          <div className={`field${fieldErrors.email ? ' invalid' : ''}`}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={core.email}
              onChange={(e) => setCore({ ...core, email: e.target.value })}
            />
            <div className="hint">Phone or email is required</div>
            {fieldErrors.email && <div className="err">{fieldErrors.email}</div>}
          </div>
        </div>

        <div className={`field${fieldErrors.campaign_id ? ' invalid' : ''}`}>
          <label htmlFor="campaign_id">Campaign</label>
          <select id="campaign_id" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">No campaign</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div className="hint">
            {campaigns.length === 0 ? 'No active campaigns yet — an admin adds them under Settings → Campaigns' : 'Active campaigns only'}
          </div>
        </div>

        {fields.length > 0 && (
          <>
            <h4 style={{ margin: '20px 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
              Details
            </h4>
            <CustomFieldSet
              fields={fields}
              values={custom}
              onChange={(key, value) => setCustom((c) => ({ ...c, [key]: value }))}
              errors={fieldErrors}
            />
          </>
        )}
      </form>
    </Modal>
  );
}
